import { useState } from 'react';
import { AlertCircle, ChevronsDown, ChevronsUp, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SupplementRoomCard } from './SupplementRoomCard';
import { useRoomSearch, useCollapseState, useTotalCalculations } from '@/hooks/useSupplementHelpers';
import { formatCents } from '@/lib/utils';
import type { DisplayRoom } from '@/types/supplement';

interface SupplementRoomBreakdownProps {
  rooms: DisplayRoom[];
  supplementId: number;
  totalSupplementCents?: number;
  totalMoneyFoundCents?: number;
}

export function SupplementRoomBreakdown({ 
  rooms,
  supplementId,
  totalSupplementCents = 0,
  totalMoneyFoundCents = 0,
}: SupplementRoomBreakdownProps) {
  const [localQuery, setLocalQuery] = useState('');
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [categorySectionsExpanded, setCategorySectionsExpanded] = useState(false);

  // Calculate totals using hook
  const { supplementTotal } = useTotalCalculations(
    rooms,
    totalSupplementCents,
    totalMoneyFoundCents
  );

  // Filter rooms using custom hook with search terms
  const filteredRooms = useRoomSearch(rooms, searchTerms);

  // Manage collapse state using custom hook
  const { allCollapsed, toggleAll, toggleItem, isCollapsed } = useCollapseState(
    filteredRooms,
    (room, index) => `${room.roomName}-${index}`
  );

  // Handle adding search term on Enter
  const handleAddSearchTerm = (e?: React.FormEvent | React.KeyboardEvent | React.MouseEvent) => {
    if (e && e.type === 'submit') e.preventDefault();

    if (!localQuery.trim()) return;

    // Add term if not already in the list
    if (!searchTerms.includes(localQuery.trim())) {
      setSearchTerms([...searchTerms, localQuery.trim()]);
    }
    setLocalQuery('');
  };

  // Handle removing a search term
  const handleRemoveSearchTerm = (termToRemove: string) => {
    setSearchTerms(searchTerms.filter(term => term !== termToRemove));
  };

  // Handle clearing all search terms
  const handleClearSearchTerms = () => {
    setSearchTerms([]);
  };

  // Handle expand/collapse all - including category sections
  const handleToggleAll = () => {
    toggleAll();
    // When expanding all, also expand category sections
    if (allCollapsed) {
      setCategorySectionsExpanded(true);
    } else {
      setCategorySectionsExpanded(false);
    }
  };

  if (!rooms || rooms.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <AlertCircle className="h-12 w-12 mb-4 text-muted-foreground/50" />
        <p className="text-lg font-medium">No supplement line items found</p>
        <p className="text-sm">The supplement may still be processing</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-20">
      {/* Header Card - Comparison Style */}
      <Card className="p-3 shadow-sm">
        <div className="flex flex-col gap-4 items-start justify-between md:flex-row">
          {/* Search Bar */}
          <div className="w-full md:w-1/3">
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-1 top-1/2 transform -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-primary z-10"
                onClick={(e) => handleAddSearchTerm(e)}
              >
                <Search className="h-4 w-4" />
              </Button>
              <Input
                placeholder={searchTerms.length > 0 ? "Add another term..." : "Search rooms or line items..."}
                value={localQuery}
                onChange={(e) => setLocalQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSearchTerm(e);
                  }
                }}
                className="pl-10 bg-background/50"
              />
            </div>
            {/* Search Term Bubbles */}
            {searchTerms.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {searchTerms.map((term, i) => (
                  <Badge key={`term-${i}`} variant="secondary" className="flex items-center gap-1 px-2 py-0.5 text-xs font-normal">
                    "{term}"
                    <button
                      onClick={() => handleRemoveSearchTerm(term)}
                      className="ml-1 hover:text-destructive focus:outline-none"
                    >
                      <span className="sr-only">Remove</span>
                      &times;
                    </button>
                  </Badge>
                ))}
                {searchTerms.length > 1 && (
                  <button onClick={handleClearSearchTerms} className="text-[10px] text-muted-foreground underline ml-1 hover:text-primary">
                    Clear terms
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Stats Summary */}
          <div className="flex w-full gap-4 text-sm md:w-auto md:justify-end">
            <div className="flex flex-col md:items-end">
              <span className="text-muted-foreground text-xs uppercase font-bold tracking-wider">Total Supplement Amount</span>
              <span className="text-green-600 dark:text-green-400 font-bold">
                {formatCents(supplementTotal)}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Title and Collapse Button */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-medium text-foreground">
          Room Breakdown ({filteredRooms.length} {filteredRooms.length === 1 ? 'Room' : 'Rooms'})
        </h3>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleToggleAll} 
          className="h-9 w-full gap-2 sm:h-auto sm:w-auto"
        >
          {allCollapsed ? (
            <>
              <ChevronsDown className="h-4 w-4" />
              Expand All
            </>
          ) : (
            <>
              <ChevronsUp className="h-4 w-4" />
              Collapse All
            </>
          )}
        </Button>
      </div>

      {/* Rooms List */}
      <div className="space-y-4">
        {filteredRooms.map((room, index) => {
          const roomKey = `${room.roomName}-${index}`;
          return (
            <SupplementRoomCard
              key={roomKey}
              room={room}
              supplementId={supplementId}
              isOpen={!isCollapsed(roomKey)}
              onOpenChange={(open) => toggleItem(roomKey, open)}
              categorySectionsOpen={categorySectionsExpanded}
            />
          );
        })}
        
        {filteredRooms.length === 0 && (
          <div className="text-center py-16 text-muted-foreground bg-muted/30 rounded-xl border border-dashed">
            No rooms match your search query.
          </div>
        )}
      </div>
    </div>
  );
}
